// apps/admin/src/features/users/AdminUserProfileSection.tsx
//
// Phase 8.2 Wave 7 Task 50 — Profile section of the Admin User Detail page.
//
// Renders an inline-edit form for name / email / status / primary role.
// On submit issues PATCH /api/v1/admin/users/:id with optimistic cache
// update so the sticky header refreshes instantly.
//
// vg-binding: PLAN:task-50
// vg-binding: P8.D-380  — every mutation emits an audit entry (visible in
//                          Activity tab on refresh)
// vg-binding: P8.D-475  — self-action guard: cannot disable own account
//                          (status select disables 'inactive'/'locked' when
//                          viewing self; banner already shown by parent)

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  updateAdminUser,
  type AdminUserDetail,
  type AdminUserApiError,
  type UpdateAdminUserRequest,
} from '../../api/admin-users.api.js';

interface AdminUserProfileSectionProps {
  user: AdminUserDetail;
  /** True iff the page is being viewed by the user themselves. */
  isSelf: boolean;
}

// Status enum values; visible labels resolved via i18n at render time.
const STATUS_VALUES: ('active' | 'inactive' | 'locked')[] = [
  'active',
  'inactive',
  'locked',
];

// Primary-role enum values; labels reuse `admin_user.roleOptions.*`.
const ROLE_VALUES = [
  'superadmin',
  'admin',
  'support',
  'saler',
  'manager',
  'accountant',
  'affiliate',
];

export function AdminUserProfileSection({
  user,
}: AdminUserProfileSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('users');

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [primaryRole, setPrimaryRole] = useState(user.primary_role);
  const [status, setStatus] = useState<string>(user.status);

  // Re-hydrate form when the underlying user changes (e.g. after an
  // invalidate-on-success refetch).
  useEffect(() => {
    setName(user.name);
    setEmail(user.email);
    setPrimaryRole(user.primary_role);
    setStatus(user.status);
  }, [user.id, user.name, user.email, user.primary_role, user.status]);

  // Only name + email are editable here. Role → grant/revoke actions; status →
  // suspend/unsuspend actions (both on the users list row). Editing them via the
  // profile PATCH bypassed those guarded flows and the BE ignored the fields
  // (→ "At least one field required"), so they are read-only on this tab.
  const dirty = useMemo(() => {
    return name !== user.name || email !== user.email;
  }, [name, email, user]);

  const mutation = useMutation({
    mutationFn: async (body: UpdateAdminUserRequest) => updateAdminUser(user.id, body),
    onMutate: async (body) => {
      // Optimistic update — patch the cached detail so the sticky header
      // (which reads from the same cache slice) flips instantly.
      await queryClient.cancelQueries({ queryKey: ['admin', 'users', user.id] });
      const prev = queryClient.getQueryData<AdminUserDetail>([
        'admin',
        'users',
        user.id,
      ]);
      if (prev) {
        queryClient.setQueryData<AdminUserDetail>(
          ['admin', 'users', user.id],
          { ...prev, ...body },
        );
      }
      return { prev };
    },
    onError: (err, _body, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['admin', 'users', user.id], ctx.prev);
      }
      const apiErr = err as unknown as AdminUserApiError;
      toast({
        variant: 'error',
        title: t('detail.profile.toast.updateFailTitle'),
        description: apiErr?.userMessage ?? t('detail.profile.toast.updateFailDesc'),
      });
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('detail.profile.toast.savedTitle'),
        description: t('detail.profile.toast.savedDesc'),
      });
      // Refetch detail + activity so the new audit row shows up.
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'users', user.id, 'activity'],
      });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!dirty || mutation.isPending) return;
    const body: UpdateAdminUserRequest = {};
    if (name !== user.name) body.name = name;
    if (email !== user.email) body.email = email;
    mutation.mutate(body);
  };

  return (
    <form
      data-test-id="admin-user-profile-form"
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-pw-text">{t('detail.profile.fields.name')}</span>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-test-id="admin-user-profile-name"
            required
            maxLength={120}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-pw-text">{t('detail.profile.fields.email')}</span>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-test-id="admin-user-profile-email"
            required
            maxLength={320}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-pw-text">{t('detail.profile.fields.primaryRole')}</span>
          <Select value={primaryRole} onValueChange={() => undefined} disabled>
            <SelectTrigger data-test-id="admin-user-profile-role">
              <SelectValue placeholder={t('detail.profile.fields.primaryRole')} />
            </SelectTrigger>
            <SelectContent>
              {ROLE_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`admin_user.roleOptions.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-pw-text-muted">
            {t('detail.profile.roleManagedHint')}
          </span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-pw-text">{t('detail.profile.fields.status')}</span>
          <Select value={status} onValueChange={() => undefined} disabled>
            <SelectTrigger data-test-id="admin-user-profile-status">
              <SelectValue placeholder={t('detail.profile.fields.status')} />
            </SelectTrigger>
            <SelectContent>
              {STATUS_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`detail.profile.statusOptions.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-pw-text-muted">
            {t('detail.profile.statusManagedHint')}
          </span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-pw-border pt-4">
        <Button
          type="submit"
          disabled={!dirty || mutation.isPending}
          data-test-id="admin-user-profile-save"
        >
          {mutation.isPending ? t('detail.profile.saving') : t('detail.profile.save')}
        </Button>
      </div>
    </form>
  );
}
